import type {
  CatalogProgressMeta,
  CatalogYearProgressMeta,
  CollectionItem,
  PublicRelease,
} from "../api/types";
import type { CollectionViewFilters } from "./collection-filters";

export type ProgressCounts = {
  uniqueCount: number;
  totalQuantity: number;
  numbersOwned: number;
  seriesPositionsOwned: number;
};

export type CollectionProgressSummary = {
  scope: "all" | "year" | "series";
  year: number | null;
  seriesName: string | null;
  uniqueCount: number;
  totalQuantity: number;
  numbersOwned: number;
  numbersTotal: number | null;
  numbersConflict: boolean;
  seriesPositionsOwned: number;
  seriesTotal: number | null;
  seriesConflict: boolean;
};

function yearMeta(
  meta: CatalogProgressMeta | null,
  year: number,
): CatalogYearProgressMeta | null {
  return meta?.years.find((row) => row.year === year) ?? null;
}

function canonicalTotal(total: number | null | undefined, conflict: boolean | undefined): number | null {
  if (conflict || total === null || total === undefined) {
    return null;
  }
  return total;
}

function countScopedCollectorNumbers(
  items: CollectionItem[],
  year: number,
  canonicalCollectorTotal: number | null,
): number {
  const numbers = new Set<number>();
  for (const item of items) {
    const release = item.release;
    if (release.year !== year || release.collectorNumber === null) {
      continue;
    }
    if (
      canonicalCollectorTotal !== null &&
      release.collectorTotal !== canonicalCollectorTotal
    ) {
      continue;
    }
    numbers.add(release.collectorNumber);
  }
  return numbers.size;
}

function countScopedSeriesPositions(
  items: CollectionItem[],
  year: number,
  seriesName: string,
  canonicalSeriesTotal: number | null,
): number {
  const positions = new Set<number>();
  for (const item of items) {
    const release = item.release;
    if (release.year !== year || (release.seriesName ?? "") !== seriesName) {
      continue;
    }
    if (release.seriesNumber === null) {
      continue;
    }
    if (canonicalSeriesTotal !== null && release.seriesTotal !== canonicalSeriesTotal) {
      continue;
    }
    positions.add(release.seriesNumber);
  }
  return positions.size;
}

export function countOwnedProgress(items: CollectionItem[]): ProgressCounts {
  let totalQuantity = 0;
  for (const item of items) {
    totalQuantity += item.quantity;
  }
  return {
    uniqueCount: items.length,
    totalQuantity,
    numbersOwned: 0,
    seriesPositionsOwned: 0,
  };
}

export function collectionProgressSummary(
  items: CollectionItem[],
  filters: CollectionViewFilters,
  meta: CatalogProgressMeta | null,
): CollectionProgressSummary {
  const counts = countOwnedProgress(items);
  const year = /^\d{4}$/.test(filters.year) ? Number(filters.year) : null;
  const seriesName = filters.series.trim() === "" ? null : filters.series;
  const selectedYearMeta = year === null ? null : yearMeta(meta, year);
  const numbersTotal = canonicalTotal(
    selectedYearMeta?.collectorTotal,
    selectedYearMeta?.collectorTotalConflict,
  );
  const numbersConflict = selectedYearMeta?.collectorTotalConflict ?? false;

  if (year !== null && seriesName !== null) {
    const seriesMeta = selectedYearMeta?.series.find((row) => row.seriesName === seriesName);
    const seriesTotal = canonicalTotal(seriesMeta?.seriesTotal, seriesMeta?.seriesTotalConflict);
    const seriesConflict = seriesMeta?.seriesTotalConflict ?? false;
    return {
      scope: "series",
      year,
      seriesName,
      uniqueCount: counts.uniqueCount,
      totalQuantity: counts.totalQuantity,
      numbersOwned: countScopedCollectorNumbers(items, year, numbersTotal),
      numbersTotal,
      numbersConflict,
      seriesPositionsOwned: countScopedSeriesPositions(items, year, seriesName, seriesTotal),
      seriesTotal,
      seriesConflict,
    };
  }

  if (year !== null) {
    return {
      scope: "year",
      year,
      seriesName: null,
      uniqueCount: counts.uniqueCount,
      totalQuantity: counts.totalQuantity,
      numbersOwned: countScopedCollectorNumbers(items, year, numbersTotal),
      numbersTotal,
      numbersConflict,
      seriesPositionsOwned: 0,
      seriesTotal: null,
      seriesConflict: false,
    };
  }

  return {
    scope: "all",
    year: null,
    seriesName: null,
    uniqueCount: counts.uniqueCount,
    totalQuantity: counts.totalQuantity,
    numbersOwned: 0,
    numbersTotal: null,
    numbersConflict: false,
    seriesPositionsOwned: 0,
    seriesTotal: null,
    seriesConflict: false,
  };
}

export type FilterProgressOption = {
  value: string;
  label: string;
  description: string;
  /** Owned count already used for description (no separate calculation). */
  owned: number;
  /** Canonical total when known; null when conflict/unknown. */
  total: number | null;
};

function formatOwnedOverTotal(owned: number, total: number | null): string {
  if (total === null || owned > total) {
    return String(owned);
  }
  return `${owned}/${total}`;
}

function countYearNumbersForFilter(
  items: CollectionItem[],
  year: number,
  canonicalTotal: number | null,
): number {
  return countScopedCollectorNumbers(items, year, canonicalTotal);
}

function countSeriesPositionsForFilter(
  items: CollectionItem[],
  year: number,
  seriesName: string,
  canonicalTotal: number | null,
): number {
  return countScopedSeriesPositions(items, year, seriesName, canonicalTotal);
}

export function yearFilterOptions(
  items: CollectionItem[],
  meta: CatalogProgressMeta | null,
): FilterProgressOption[] {
  const years = [...new Set(items.map((item) => item.release.year))].sort((a, b) => b - a);
  return years.map((year) => {
    const row = yearMeta(meta, year);
    const total = canonicalTotal(row?.collectorTotal, row?.collectorTotalConflict);
    const owned = countYearNumbersForFilter(items, year, total);
    return {
      value: String(year),
      label: String(year),
      description:
        total === null
          ? `${year}, собрано ${owned} номера`
          : `${year}, собрано ${formatOwnedOverTotal(owned, total)} номера`,
      owned,
      total,
    };
  });
}

export function seriesFilterOptions(
  items: CollectionItem[],
  year: number,
  meta: CatalogProgressMeta | null,
): FilterProgressOption[] {
  const names = new Set<string>();
  for (const item of items) {
    if (item.release.year !== year) {
      continue;
    }
    const name = item.release.seriesName?.trim() ?? "";
    if (name !== "") {
      names.add(name);
    }
  }
  const yearRow = yearMeta(meta, year);
  return [...names]
    .sort((a, b) => a.localeCompare(b, "en"))
    .map((seriesName) => {
      const row = yearRow?.series.find((series) => series.seriesName === seriesName);
      const total = canonicalTotal(row?.seriesTotal, row?.seriesTotalConflict);
      const owned = countSeriesPositionsForFilter(items, year, seriesName, total);
      return {
        value: seriesName,
        label: seriesName,
        description:
          total === null
            ? `${seriesName}, собрано ${owned} позиций`
            : `${seriesName}, собрано ${formatOwnedOverTotal(owned, total)} позиций`,
        owned,
        total,
      };
    });
}

function ratioLabel(prefix: string, owned: number, total: number | null, conflict: boolean): string {
  if (conflict || total === null || owned > total) {
    return `${prefix} ${owned}`;
  }
  return `${prefix} ${owned}/${total}`;
}

export function formatProgressLabel(summary: CollectionProgressSummary): {
  detail: string;
} {
  const variants = `Варианты ${summary.uniqueCount}`;
  const total = `Всего ${summary.totalQuantity}`;

  if (summary.scope === "all") {
    return {
      detail: `${variants} · ${total}`,
    };
  }

  if (summary.scope === "year") {
    const numbers = ratioLabel(
      "Номера",
      summary.numbersOwned,
      summary.numbersTotal,
      summary.numbersConflict,
    );
    return {
      detail: `${numbers} · ${variants} · ${total}`,
    };
  }

  const inSeries = ratioLabel(
    "В серии",
    summary.seriesPositionsOwned,
    summary.seriesTotal,
    summary.seriesConflict,
  );
  return {
    detail: `${inSeries} · ${variants} · ${total}`,
  };
}

export type ProfileCollectionStats = {
  totalQuantity: number;
  uniqueCount: number;
  numbersOwned: number;
  numbersTotal: number | null;
  numbersConflict: boolean;
  thCount: number;
  sthCount: number;
  exclusiveCount: number;
  years: FilterProgressOption[];
};

/**
 * Profile statistics from the owned collection snapshot + catalog progress-meta.
 * Does not fetch the full catalog.
 */
export function profileCollectionStats(
  items: CollectionItem[],
  meta: CatalogProgressMeta | null,
): ProfileCollectionStats {
  const counts = countOwnedProgress(items);
  let numbersOwned = 0;
  let numbersTotalSum = 0;
  let numbersTotalKnown = true;
  let numbersConflict = false;

  const yearsInCollection = new Set(items.map((item) => item.release.year));
  for (const year of yearsInCollection) {
    const row = yearMeta(meta, year);
    const total = canonicalTotal(row?.collectorTotal, row?.collectorTotalConflict);
    if (row?.collectorTotalConflict) {
      numbersConflict = true;
    }
    numbersOwned += countScopedCollectorNumbers(items, year, total);
    if (total === null) {
      numbersTotalKnown = false;
    } else {
      numbersTotalSum += total;
    }
  }

  let thCount = 0;
  let sthCount = 0;
  let exclusiveCount = 0;
  for (const item of items) {
    if (item.release.huntType === "TH") {
      thCount += 1;
    } else if (item.release.huntType === "STH") {
      sthCount += 1;
    }
    if (item.release.isExclusive) {
      exclusiveCount += 1;
    }
  }

  return {
    totalQuantity: counts.totalQuantity,
    uniqueCount: counts.uniqueCount,
    numbersOwned,
    numbersTotal:
      yearsInCollection.size === 0 || !numbersTotalKnown || numbersConflict
        ? null
        : numbersTotalSum,
    numbersConflict,
    thCount,
    sthCount,
    exclusiveCount,
    years: yearFilterOptions(items, meta),
  };
}

/** @internal test helper for scoped collector counting on raw releases */
export function countCollectorNumbersInScope(
  releases: PublicRelease[],
  year: number,
  canonicalCollectorTotal: number | null,
): number {
  const items = releases.map((release) => ({
    releaseId: release.releaseId,
    quantity: 1,
    release,
  }));
  return countScopedCollectorNumbers(items, year, canonicalCollectorTotal);
}
